/**
 * Native Carousel Web Component
 * Provides drag-to-scroll functionality for desktop users
 * Touch scrolling works natively on mobile devices
 * 
 * Usage:
 * <native-carousel>
 *   <div class="native-carousel">
 *     <div class="native-carousel__slide">...</div>
 *   </div>
 * </native-carousel>
 */

if (!customElements.get('native-carousel')) {

class NativeCarousel extends HTMLElement {
  constructor() {
    super();
    this.isDown = false;
    this.startX = 0;
    this.scrollLeft = 0;
    this.velocity = 0;
    this.animationFrame = null;
    this.hasMoved = false;
    
    // Bind methods
    this.handleMouseDown = this.handleMouseDown.bind(this);
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleMouseUp = this.handleMouseUp.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
    this.handleClick = this.handleClick.bind(this);
  }

  connectedCallback() {
    this.container = this.querySelector('.native-carousel');
    if (!this.container) return;

    // Only enable drag scroll on non-touch devices
    if (!this.isTouchDevice()) {
      this.enableDragScroll();
    }

    // Set up navigation buttons if present
    this.setupNavigation();

    // Watch for content changes (e.g., dynamically loaded recommendations)
    this.observer = new MutationObserver(() => {
      this.updateNavButtons();
    });
    this.observer.observe(this.container, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    this.disableDragScroll();
    this.cleanupNavigation();
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  isTouchDevice() {
    return (
      'ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      navigator.msMaxTouchPoints > 0
    );
  }

  enableDragScroll() {
    if (!this.container) return;

    this.container.addEventListener('mousedown', this.handleMouseDown);
    this.container.addEventListener('mousemove', this.handleMouseMove);
    this.container.addEventListener('mouseup', this.handleMouseUp);
    this.container.addEventListener('mouseleave', this.handleMouseLeave);
    
    // Prevent click on links during drag
    this.container.addEventListener('click', this.handleClick, true);
  }

  disableDragScroll() {
    if (!this.container) return;

    this.container.removeEventListener('mousedown', this.handleMouseDown);
    this.container.removeEventListener('mousemove', this.handleMouseMove);
    this.container.removeEventListener('mouseup', this.handleMouseUp);
    this.container.removeEventListener('mouseleave', this.handleMouseLeave);
    this.container.removeEventListener('click', this.handleClick, true);
  }

  handleMouseDown(e) {
    // Ignore if clicking on interactive elements
    if (e.target.closest('button, a, input, select, textarea')) {
      return;
    }

    this.isDown = true;
    this.hasMoved = false;
    this.container.classList.add('is-dragging');
    this.startX = e.pageX - this.container.offsetLeft;
    this.scrollLeft = this.container.scrollLeft;
    this.velocity = 0;
    this.lastX = e.pageX;
    this.lastTime = Date.now();

    // Cancel any ongoing momentum animation
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
  }

  handleMouseMove(e) {
    if (!this.isDown) return;

    e.preventDefault();
    
    const x = e.pageX - this.container.offsetLeft;
    const walk = (x - this.startX) * 1.5; // Multiplier for sensitivity
    
    // Calculate velocity for momentum
    const now = Date.now();
    const dt = now - this.lastTime;
    if (dt > 0) {
      this.velocity = (e.pageX - this.lastX) / dt;
    }
    this.lastX = e.pageX;
    this.lastTime = now;

    // Mark as moved if we've scrolled more than 5px (prevents accidental drag)
    if (Math.abs(walk) > 5) {
      this.hasMoved = true;
    }

    this.container.scrollLeft = this.scrollLeft - walk;
  }

  handleMouseUp() {
    if (!this.isDown) return;

    this.isDown = false;
    this.container.classList.remove('is-dragging');

    // Apply momentum scrolling
    if (Math.abs(this.velocity) > 0.1) {
      this.applyMomentum();
    }
  }

  handleMouseLeave() {
    if (!this.isDown) return;
    this.handleMouseUp();
  }

  handleClick(e) {
    // Prevent click if we dragged
    if (this.hasMoved) {
      e.preventDefault();
      e.stopPropagation();
    }
  }

  applyMomentum() {
    const friction = 0.95;
    const minVelocity = 0.1;

    const animate = () => {
      if (Math.abs(this.velocity) < minVelocity) {
        this.animationFrame = null;
        return;
      }

      this.container.scrollLeft -= this.velocity * 16; // 16ms frame time
      this.velocity *= friction;

      this.animationFrame = requestAnimationFrame(animate);
    };

    this.animationFrame = requestAnimationFrame(animate);
  }

  setupNavigation() {
    this.prevBtn = this.querySelector('[data-carousel-prev]');
    this.nextBtn = this.querySelector('[data-carousel-next]');

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.scrollBy(-1));
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.scrollBy(1));
    }

    // Update button states on scroll
    if (this.prevBtn || this.nextBtn) {
      this.container?.addEventListener('scroll', () => this.updateNavButtons());
      this.updateNavButtons();
    }
  }

  cleanupNavigation() {
    // Navigation buttons are cleaned up when element is removed
  }

  scrollBy(direction) {
    if (!this.container) return;

    // Find slide - check for both generic slides and recommendation items
    const slide = this.container.querySelector('.native-carousel__slide, .cart-drawer__recommendation-item');
    if (!slide) return;

    const slideWidth = slide.offsetWidth;
    const gap = parseInt(getComputedStyle(this.container).gap) || 0;
    const scrollAmount = (slideWidth + gap) * direction;

    this.container.scrollBy({
      left: scrollAmount,
      behavior: 'smooth'
    });
  }

  updateNavButtons() {
    if (!this.container) return;

    const scrollLeft = this.container.scrollLeft;
    const maxScroll = this.container.scrollWidth - this.container.clientWidth;
    
    // Use a threshold to account for spacers/padding (e.g., ::before pseudo-elements)
    const threshold = 20;

    if (this.prevBtn) {
      this.prevBtn.disabled = scrollLeft <= threshold;
    }
    if (this.nextBtn) {
      this.nextBtn.disabled = scrollLeft >= maxScroll - threshold;
    }
  }

  // Public method to scroll to a specific slide
  scrollToSlide(index) {
    if (!this.container) return;

    const slides = this.container.querySelectorAll('.native-carousel__slide');
    if (index < 0 || index >= slides.length) return;

    slides[index].scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'start'
    });
  }

  // Public method to get current slide index
  getCurrentSlide() {
    if (!this.container) return 0;

    const slides = this.container.querySelectorAll('.native-carousel__slide');
    const containerRect = this.container.getBoundingClientRect();

    for (let i = 0; i < slides.length; i++) {
      const slideRect = slides[i].getBoundingClientRect();
      if (slideRect.left >= containerRect.left - 10) {
        return i;
      }
    }

    return 0;
  }
}

// Register custom element
customElements.define('native-carousel', NativeCarousel);

} // end if (!customElements.get('native-carousel'))

